package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.withPlan
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.defaultWorkplaceId
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_no_workplace_access
import evoshift.composeapp.generated.resources.error_user_not_found
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.capabilities
import com.evoapps.evoshift.domain.usecase.SendAvailabilityRequestUseCase
import com.evoapps.evoshift.domain.usecase.availability.RequestAvailabilityForMonthUseCase
import evoshift.composeapp.generated.resources.availability_request_sent
import com.evoapps.evoshift.domain.model.AvailabilityEmailLinks
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.roster.DeleteRosterUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.ExperimentalCoroutinesApi
import evoshift.composeapp.generated.resources.roster_deleted
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class DashboardUiState(
    val isLoading: Boolean = true,
    val userId: String = "",
    val userName: String = "",
    val userPermission: UserPermission? = null,
    /** What the person may do at the current workplace (empty once the engagement ended): what the app shows. */
    val capabilities: Set<Capability> = emptySet(),
    val currentWorkplaceId: String? = null,     // Critical for context switching
    val activeRosters: List<Roster> = emptyList(),
    /** The current workplace's rosters are on their way (none known yet): not "no rosters". */
    val isLoadingRosters: Boolean = false,
    /** SH-168: the workplace's rosters could not be read (none shown): Try again, not "no rosters". */
    val rostersLoadError: UiText? = null,
    /** SH-168: a pull-to-refresh is reading the rosters again. */
    val isRefreshing: Boolean = false,
    val error: UiText? = null,
    /** SH-172: a one-off confirmation for the snackbar (the availability request went). */
    val notice: UiText? = null,
    // List of available engagements for the "Context Switcher" bottom sheet
    val availableWorkplaceIds: Map<String, String> = emptyMap()
) {
    /**
     * SH-247: whether the current workplace's rosters are known: an empty [activeRosters] is
     * then "no rosters yet", before it only "not read yet" (placeholders, not the empty state).
     * Rosters already shown (the cache, a workplace seen before) are known.
     */
    val rostersKnown: Boolean
        get() = activeRosters.isNotEmpty() || rostersLoadError != null ||
            !(isLoadingRosters || (isLoading && currentWorkplaceId == null))
}


sealed interface DashboardEvent {
    /** Re-checks the session's workplace and (SH-168) reads its rosters again (pull-to-refresh, Try again). */
    data object Refresh : DashboardEvent
    data class SelectWorkplace(val workplaceId: String) : DashboardEvent
    data class JoinWorkspaceByCode(val code: String) : DashboardEvent
    data class RequestAvailability(
        val rosterId: String,
        val month: String,
        val startDate: String,
        val endDate: String,
        val dueDate: String,
        val message: String?,
        /** SH-173: also by e-mail with a personal link to the web form. */
        val emailLinks: AvailabilityEmailLinks = AvailabilityEmailLinks.NONE
    ) : DashboardEvent
    data class DeleteRoster(val rosterId: String) : DashboardEvent
    /**
     * SH-172: asks for availability for [year]-[month] at any stage, also before its roster
     * exists (an empty one is created for the request).
     */
    data class RequestAvailabilityForMonth(
        val year: Int,
        val month: Int,
        val startDate: String,
        val endDate: String,
        val dueDate: String,
        val message: String?,
        val emailLinks: AvailabilityEmailLinks = AvailabilityEmailLinks.NONE
    ) : DashboardEvent
    data object NoticeShown : DashboardEvent
}

class DashboardViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val sendAvailabilityRequestUseCase: SendAvailabilityRequestUseCase,
    private val deleteRosterUseCase: DeleteRosterUseCase,
    private val sessionManager: WorkplaceSessionManager,
    /** The workplace's plan: once it ended, everything is read-only (SH-65). */
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-172: availability for a month without a roster (or any month); null: only rosters' own. */
    private val requestForMonth: RequestAvailabilityForMonthUseCase? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(DashboardUiState())
    val uiState: StateFlow<DashboardUiState> = _uiState.asStateFlow()

    /** The last rosters seen per workplace: switching back shows them at once, then the stream updates them. */
    private val rostersByWorkplace = mutableMapOf<String, List<Roster>>()

    /** SH-168: bumped by [DashboardEvent.Refresh]: the rosters' listener starts again. */
    private val attempts = MutableStateFlow(0)

    /**
     * SH-248: whose workplace view (role, capabilities, rosters) the state holds; null before the
     * first one. Until the signed-in person's arrives the dashboard is loading, not "Error".
     */
    private var viewUserId: String? = null

    init {
        observeUser()
        observeWorkplace()
    }

    /** The person and their workplaces; keeps the session on a workplace they belong to. */
    private fun observeUser() {
        viewModelScope.launch {
            getCurrentUserUseCase().collect { user ->
                if (user == null) {
                    _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_user_not_found)) }
                    return@collect
                }
                // SH-248, the owner's report from a fresh iPad: this view model starts with the
                // sign-in screen, signed out, which ended "loading" above. Signing in then gave the
                // workplaces at once but the role only once the workplace's plan and rosters had
                // arrived (slow on a first start with an empty cache), and in between the dashboard
                // showed its "Error" for a workplace without a role. Until the person's own view
                // arrives it is loading again; the previous person's role and rosters go.
                if (viewUserId != user.uid) {
                    _uiState.update {
                        it.copy(
                            isLoading = true, error = null, userPermission = null, capabilities = emptySet(),
                            activeRosters = emptyList(), isLoadingRosters = false, rostersLoadError = null
                        )
                    }
                }
                // Ended engagements are history, not workplaces to open (the rules refuse them).
                val workplaces = user.engagements.filterValues { it.isActive }.mapValues {
                    it.value.cachedWorkplaceName?.takeIf { name -> name != "TBD" && name.isNotBlank() }.orEmpty()
                }
                _uiState.update { it.copy(userId = user.uid, userName = user.displayName, availableWorkplaceIds = workplaces) }
                val current = sessionManager.currentWorkplaceId.value
                when {
                    workplaces.isEmpty() -> _uiState.update { it.copy(isLoading = false, currentWorkplaceId = null) }
                    // The primary workplace first (not the map's arbitrary first key).
                    current == null || current !in workplaces -> sessionManager.setWorkplaceId(user.defaultWorkplaceId())
                }
            }
        }
    }

    /**
     * The current workplace, the person's role there and its rosters, as one stream. Switching
     * the workplace replaces the roster listener (an old one kept writing its own workplace's
     * rosters, so the list flipped between workplaces); profile updates that change neither the
     * workplace nor the role do not reload anything.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    private fun observeWorkplace() {
        viewModelScope.launch {
            // The workplace together with its plan, so a switch never pairs one with the other's plan.
            val workplaceAndPlan = sessionManager.currentWorkplaceId.flatMapLatest { workplaceId ->
                observePlanActive.activeAt(workplaceId).map { planActive -> workplaceId to planActive }
            }
            combine(getCurrentUserUseCase(), workplaceAndPlan, attempts) { user, (workplaceId, planActive), attempt ->
                // An ended engagement is no role at the workplace (the rules refuse it everything).
                val engagement = workplaceId?.let { user?.engagements?.get(it) }?.takeIf { it.isActive }
                WorkplaceKey(workplaceId, engagement?.permission, engagement?.capabilities.orEmpty().withPlan(planActive), attempt, user?.uid)
            }
                .distinctUntilChanged()
                .flatMapLatest { (workplaceId, permission, capabilities, _, userId) ->
                    when {
                        // SH-248: signed out there is no view: this listener can still see nobody
                        // while the other (observeUser) has the new person and their workplace, and
                        // "no access" for nobody would end the person's loading.
                        workplaceId == null || userId == null -> flowOf(null)
                        permission == null -> flowOf(WorkplaceView(workplaceId, null, emptySet(), emptyList(), userId = userId))
                        // Only those who build rosters list them here (Rosters, the month switcher).
                        Capability.EDIT_ROSTER in capabilities -> getRosterSummaryUseCase(workplaceId)
                            .map { rosters -> WorkplaceView(workplaceId, permission, capabilities, rosters.sortedByDescending { it.year * 100 + it.month }, userId = userId) }
                            // SH-168: a failed listener keeps what was shown and says so (Try again).
                            .catch { e ->
                                emit(WorkplaceView(workplaceId, permission, capabilities, rostersByWorkplace[workplaceId].orEmpty(), loadError = e.toUiText(), userId = userId))
                            }
                        else -> flowOf(WorkplaceView(workplaceId, permission, capabilities, emptyList(), userId = userId))
                    }
                }
                .collect { view ->
                    if (view == null) {
                        _uiState.update { it.copy(isRefreshing = false) }
                        return@collect
                    }
                    if (view.loadError == null) rostersByWorkplace[view.workplaceId] = view.rosters
                    viewUserId = view.userId
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            currentWorkplaceId = view.workplaceId,
                            userPermission = view.permission,
                            capabilities = view.capabilities,
                            activeRosters = view.rosters,
                            isLoadingRosters = false,
                            rostersLoadError = view.loadError,
                            isRefreshing = false,
                            error = if (view.permission == null) UiText.Resource(Res.string.error_no_workplace_access) else null
                        )
                    }
                }
        }
    }

    private data class WorkplaceView(
        val workplaceId: String,
        val permission: UserPermission?,
        val capabilities: Set<Capability>,
        val rosters: List<Roster>,
        val loadError: UiText? = null,
        /** Whose view this is (SH-248); null signed out. */
        val userId: String? = null
    )

    private data class WorkplaceKey(
        val workplaceId: String?,
        val permission: UserPermission?,
        val capabilities: Set<Capability>,
        val attempt: Int,
        val userId: String?
    )

    fun onEvent(event: DashboardEvent) {
        when (event) {
            // The data is live; a refresh only re-checks that the session's workplace is valid.
            DashboardEvent.Refresh -> {
                val current = sessionManager.currentWorkplaceId.value
                if (current != null && current !in _uiState.value.availableWorkplaceIds) sessionManager.setWorkplaceId(null)
                // SH-168: the rosters' listener starts again (after a failure, or a pull-to-refresh).
                val showsRosters = Capability.EDIT_ROSTER in _uiState.value.capabilities
                _uiState.update { it.copy(isRefreshing = showsRosters, isLoadingRosters = showsRosters && it.activeRosters.isEmpty()) }
                attempts.value++
            }
            is DashboardEvent.SelectWorkplace -> {
                // Show the new workplace right away; its rosters follow from the stream.
                if (event.workplaceId != _uiState.value.currentWorkplaceId) {
                    val known = rostersByWorkplace[event.workplaceId]
                    _uiState.update {
                        it.copy(currentWorkplaceId = event.workplaceId, activeRosters = known.orEmpty(), isLoadingRosters = known == null)
                    }
                }
                sessionManager.setWorkplaceId(event.workplaceId)
            }
            is DashboardEvent.JoinWorkspaceByCode -> joinWorkspaceByCode(event.code)
            is DashboardEvent.RequestAvailability -> requestAvailability(event)
            is DashboardEvent.DeleteRoster -> deleteRoster(event.rosterId)
            is DashboardEvent.RequestAvailabilityForMonth -> requestAvailabilityForMonth(event)
            DashboardEvent.NoticeShown -> _uiState.update { it.copy(notice = null) }
        }
    }

    private fun joinWorkspaceByCode(code: String) {

    }

    private fun requestAvailability(event: DashboardEvent.RequestAvailability) {
        if (!_uiState.value.capabilities.shows(Gate.AVAILABILITY_CAMPAIGN)) return
        val workplaceId = _uiState.value.currentWorkplaceId ?: return
        val userId = _uiState.value.userId

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val result = sendAvailabilityRequestUseCase(
                managerId = userId,
                workplaceId = workplaceId,
                rosterId = event.rosterId,
                month = event.month,
                startDate = event.startDate,
                endDate = event.endDate,
                dueDate = event.dueDate,
                message = event.message,
                emailLinks = event.emailLinks
            )

            if (result.isSuccess) {
                // The roster list refreshes through its listener; SH-188: said in the snackbar.
                _uiState.update { it.copy(isLoading = false, notice = UiText.Resource(Res.string.availability_request_sent)) }
            } else {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = result.exceptionOrNull()?.toUiText()
                    )
                }
            }
        }
    }

    /** Whether availability can be asked for a month without a roster here (SH-172). */
    val canRequestForAnyMonth: Boolean get() = requestForMonth != null

    private fun requestAvailabilityForMonth(event: DashboardEvent.RequestAvailabilityForMonth) {
        val request = requestForMonth ?: return
        if (!_uiState.value.capabilities.shows(Gate.AVAILABILITY_CAMPAIGN)) return
        val workplaceId = _uiState.value.currentWorkplaceId ?: return
        val userId = _uiState.value.userId
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            request(userId, workplaceId, event.year, event.month, event.startDate, event.endDate, event.dueDate, event.message, event.emailLinks).fold(
                // The new (or updated) roster arrives through the rosters' stream.
                onSuccess = { _uiState.update { it.copy(isLoading = false, notice = UiText.Resource(Res.string.availability_request_sent)) } },
                // Said in the snackbar (the screen's error is the workplace's access).
                onFailure = { e -> _uiState.update { it.copy(isLoading = false, notice = e.toUiText()) } }
            )
        }
    }

    private fun deleteRoster(rosterId: String) {
        if (!_uiState.value.capabilities.shows(Gate.ROSTER_EDITING)) return
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            val result = deleteRosterUseCase(rosterId)
            if (result.isSuccess) {
                // SH-188: the month is gone from the list; said in the snackbar.
                _uiState.update { it.copy(isLoading = false, notice = UiText.Resource(Res.string.roster_deleted)) }
            } else {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        error = result.exceptionOrNull()?.toUiText()
                    )
                }
            }
        }
    }
}
